import * as React from "react";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";

type SocialPlatform = "facebook" | "instagram" | "threads" | "bluesky" | "youtube" | "tiktok";

const accounts: { name: string; platform: SocialPlatform; href: string }[] = [
  { name: "Facebook", platform: "facebook", href: "https://www.facebook.com/profile.php?id=100089226551514" },
  { name: "Instagram", platform: "instagram", href: "https://www.instagram.com/emilybarlowwritesthings/" },
  { name: "Threads", platform: "threads", href: "https://www.threads.com/@emilybarlowwritesthings" },
  { name: "Bluesky", platform: "bluesky", href: "https://bsky.app/profile/emilybarlowauthor.bsky.social" },
  { name: "YouTube", platform: "youtube", href: "https://www.youtube.com/@EmilyBarlowWritesThings" },
  { name: "TikTok", platform: "tiktok", href: "https://www.tiktok.com/@e.barlow.writes.things" },
];

function BrandIcon({ platform }: { platform: SocialPlatform }) {
  const svgProps = {
    viewBox: "0 0 24 24",
    width: "1em",
    height: "1em",
    fill: "currentColor",
    "aria-hidden": true as const,
    focusable: false as const,
  };

  switch (platform) {
    case "facebook":
      return <FacebookIcon fontSize="inherit" />;
    case "instagram":
      return <InstagramIcon fontSize="inherit" />;
    case "threads":
      return <svg {...svgProps}><path d="M12.04 2.1c-5.45 0-8.7 3.65-8.7 9.77 0 6.04 3.2 10.03 8.7 10.03 4.2 0 7.1-2.35 7.1-6.02 0-2.82-1.65-4.62-4.6-5.02-.17-1.24-.7-1.9-1.7-1.9-.87 0-1.48.48-1.82 1.35-.55-.15-1.08-.2-1.56-.2-2.08 0-3.34 1.14-3.34 2.88 0 1.72 1.28 2.82 3.28 2.82 1.83 0 3.05-.9 3.4-2.58 1.7.3 2.54 1.14 2.54 2.65 0 2.45-1.88 3.96-5.1 3.96-4.17 0-6.58-3.08-6.58-7.97 0-4.95 2.44-7.76 6.6-7.76 3.25 0 5.3 1.54 6.08 4.58l2.23-.56C17.55 4.12 15.3 2.1 12.04 2.1Zm-2.54 11.7c-.9 0-1.35-.3-1.35-.85 0-.6.5-.92 1.4-.92.42 0 .9.07 1.4.2-.13 1.05-.6 1.57-1.45 1.57Z" /></svg>;
    case "bluesky":
      return <svg {...svgProps}><path d="M5.2 3.2C8 5.3 10.9 9.6 12 11.9c1.1-2.3 4-6.6 6.8-8.7 2-1.5 5.2-2.7 5.2 1.1 0 .8-.5 6.6-.8 7.6-.9 3.2-4.1 4-7 3.4 5.1.9 6.4 3.9 3.6 6.9-5.3 5.6-7.6-1.4-8.2-3.6-.1-.4-.2-.6-.3-.4-.1-.2-.2 0-.3.4-.6 2.2-2.9 9.2-8.2 3.6-2.8-3-.1-6 3.6-6.9-2.9.6-6.1-.2-7-3.4C-.9 11-.9 5.1-.9 4.3c0-3.8 3.2-2.6 5.2-1.1Z" transform="translate(1.2 0) scale(.92)" /></svg>;
    case "youtube":
      return <svg {...svgProps}><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.3 3.6-6.3 3.6Z" /></svg>;
    case "tiktok":
      return <svg {...svgProps}><path d="M19.6 7.2a6.8 6.8 0 0 1-4.2-1.5v8.1a6.2 6.2 0 1 1-5.4-6.1v3.2a3.1 3.1 0 1 0 2.2 3V0h3.2c.2 2.7 1.8 4.5 4.2 4.8v2.4Z" /></svg>;
  }
}

export function SocialLinks({ location, showLabels = false }: { location: "header" | "footer"; showLabels?: boolean }) {
  return (
    <BoxLinks location={location}>
      {accounts.map(({ name, platform, href }) => (
        <a
          key={platform}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visit Emily Barlow on ${name} (opens in a new tab)`}
          title={name}
          style={{
            color: "inherit",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            fontSize: location === "header" ? "1.25rem" : "1.15rem",
            lineHeight: 1,
            textDecoration: "none",
            minWidth: "24px",
            minHeight: "24px",
          }}
        >
          <BrandIcon platform={platform} />
          {showLabels && <span style={{ fontSize: "0.9rem" }}>{name}</span>}
        </a>
      ))}
    </BoxLinks>
  );
}

function BoxLinks({ location, children }: React.PropsWithChildren<{ location: "header" | "footer" }>) {
  return (
    <div
      aria-label="Social media links"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: location === "footer" ? "flex-start" : "center",
        flexWrap: "wrap",
        gap: location === "header" ? "12px" : "18px",
      }}
    >
      {children}
    </div>
  );
}
